import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Crown, Mail, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';

const DEFAULTS = {
  title: 'A Intimate.pt está prestes a abrir as portas.',
  subtitle: 'Estamos a preparar os últimos detalhes para te proporcionar a melhor experiência de leitura erótica para adultos.',
  description: 'Muito em breve vais poder explorar contos, séries, conteúdos educativos e muito mais num ambiente discreto, elegante e exclusivo.',
  footer: 'Conteúdo exclusivo para maiores de 18 anos.',
};

export default function MaintenanceMode({ initialConfig = null }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(initialConfig || DEFAULTS);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialConfig) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/maintenance/status');
        if (!cancelled) setConfig(data);
      } catch {
        if (!cancelled) setConfig(DEFAULTS);
      }
    })();
    return () => { cancelled = true; };
  }, [initialConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Insere um email válido');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/maintenance/subscribe', { email });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Não foi possível guardar o email. Tenta novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex flex-col">
      {/* Decorative glows */}
      <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-ruby/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[26rem] h-[26rem] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 200 200%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.6%27/></svg>")',
        }}
      />

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 sm:px-6 py-12">
        <div className="w-full max-w-2xl text-center">
          {/* Title */}
          <h2
            className="font-brand text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-5 text-white"
            data-testid="maintenance-title"
          >
            {config.title || DEFAULTS.title}
          </h2>

          {/* Subtitle */}
          <p
            className="text-base sm:text-lg text-gray-300 mb-6 leading-relaxed"
            data-testid="maintenance-subtitle"
          >
            {config.subtitle || DEFAULTS.subtitle}
          </p>

          {/* Description */}
          <p
            className="text-sm sm:text-base text-gray-400 mb-10 max-w-xl mx-auto leading-relaxed"
            data-testid="maintenance-description"
          >
            {config.description || DEFAULTS.description}
          </p>

          {/* Email capture */}
          {config.email_capture_enabled && (
            <div className="max-w-md mx-auto mb-12">
              {submitted ? (
                <div
                  className="flex items-center gap-3 justify-center rounded-2xl border border-green-500/30 bg-green-500/10 px-5 py-4 text-green-200"
                  data-testid="maintenance-submitted"
                >
                  <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                  <p className="text-sm font-medium">
                    Obrigado! Avisamos-te assim que abrirmos as portas.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="maintenance-form">
                  <label
                    htmlFor="maintenance-email"
                    className="text-xs uppercase tracking-widest text-gray-400 flex items-center justify-center gap-2"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Receber aviso de lançamento
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      id="maintenance-email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="o-teu@email.com"
                      data-testid="maintenance-email-input"
                      className="flex-1 px-4 py-3 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-ruby focus:ring-2 focus:ring-ruby/20 text-base"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="maintenance-submit"
                      className="px-6 py-3 rounded-full bg-ruby hover:bg-ruby-light text-white font-semibold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Crown className="w-4 h-4" />
                          Avisa-me
                        </>
                      )}
                    </button>
                  </div>
                  {error && (
                    <p className="text-sm text-red-400" data-testid="maintenance-error">
                      {error}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    Não enviaremos spam. Apenas um email no dia do lançamento.
                  </p>
                </form>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="pt-8 border-t border-white/10">
            <p
              className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-400"
              data-testid="maintenance-footer"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-gold" />
              {config.footer || DEFAULTS.footer}
            </p>
          </div>
        </div>
      </main>

      {/* Subtle admin login hint */}
      <footer className="relative z-10 px-6 pb-6 text-center">
        <button
          type="button"
          onClick={() => navigate('/login')}
          data-testid="maintenance-admin-login"
          className="text-xs text-gray-600 hover:text-gray-400 transition-colors"
        >
          Acesso de administrador →
        </button>
      </footer>
    </div>
  );
}
